import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { toFriendlyMutationError } from "@/lib/error-utils";
import { generateUUID } from "@/lib/utils";

export type InvoiceStatus = "draft" | "sent" | "paid" | "cancelled";
export type InvoiceCurrency = "CHF" | "EUR";
export type InvoiceReminderStatus = "sent" | "paid" | "cancelled";

export interface Invoice {
  id: string;
  project_id: string | null;
  source_quote_version_id: string | null;
  invoice_number: number;
  title: string;
  language: "de" | "en" | "fr" | null;
  currency: InvoiceCurrency;
  status: InvoiceStatus;
  invoice_date: string | null;
  due_date: string | null;
  payment_term_days: number;
  project_discount_percent: number;
  vat_rate: "CH_8_1" | "NONE";
  deposit_percent: number | null;
  qr_reference: string | null;
  include_qr_bill: boolean;
  pdf_url: string | null;
  notes: string | null;
  total_net: number;
  total_gross: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  customer_id: string | null;
  contact_id: string | null;
  customer?: {
    name: string;
    invoice_address: string | null;
    number: string | null;
  } | null;
  contact?: { first_name: string; last_name: string } | null;
  project?: {
    id: string;
    name: string;
    number: string | null;
    language: string | null;
    default_vat_rate: "CH_8_1" | "NONE" | null;
    default_discount_percent: number | null;
    event_start_date: string | null;
    event_end_date: string | null;
    planning_start_date: string | null;
    planning_end_date: string | null;
    companies?: {
      name: string;
      invoice_address: string | null;
      number: string | null;
    } | null;
    contact?: { first_name: string; last_name: string } | null;
    account_manager?: { first_name: string; last_name: string } | null;
  } | null;
}

export interface InvoiceReminder {
  id: string;
  invoice_id: string;
  reminder_number: number;
  title: string;
  reminder_date: string;
  due_date: string;
  payment_term_days: number;
  pdf_url: string | null;
  status: InvoiceReminderStatus;
  total_net: number;
  total_gross: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  invoice?: Invoice;
}

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

type ZeroInvoiceRow = {
  id: string;
  projectID?: string | null;
  sourceQuoteVersionID?: string | null;
  customerID?: string | null;
  contactID?: string | null;
  invoiceNumber?: number | null;
  title: string;
  language?: "de" | "en" | "fr" | null;
  currency: InvoiceCurrency;
  status: InvoiceStatus;
  invoiceDate?: number | string | null;
  dueDate?: number | string | null;
  paymentTermDays: number;
  projectDiscountPercent: number;
  vatRate: "CH_8_1" | "NONE";
  depositPercent?: number | null;
  qrReference?: string | null;
  includeQRBill: boolean;
  pdfURL?: string | null;
  notes?: string | null;
  totalNet: number;
  totalGross: number;
  createdBy?: string | null;
  deletedAt?: number | string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  customer?: {
    name: string;
    invoiceAddress?: string | null;
    number?: string | null;
  } | null;
  contact?: { firstName: string; lastName: string } | null;
  project?: {
    id: string;
    name: string;
    number?: string | null;
    language?: string | null;
    defaultVatRate?: "CH_8_1" | "NONE" | null;
    defaultDiscountPercent?: number | null;
    eventStartDate?: number | string | null;
    eventEndDate?: number | string | null;
    planningStartDate?: number | string | null;
    planningEndDate?: number | string | null;
    companies?: {
      name: string;
      invoiceAddress?: string | null;
      number?: string | null;
    } | null;
    contact?: { firstName: string; lastName: string } | null;
    account_manager?: { firstName: string; lastName: string } | null;
  } | null;
};

type ZeroInvoiceReminderRow = {
  id: string;
  invoiceID: string;
  reminderNumber: number;
  title: string;
  reminderDate: number | string;
  dueDate: number | string;
  paymentTermDays: number;
  pdfURL?: string | null;
  status: InvoiceReminderStatus;
  totalNet: number;
  totalGross: number;
  createdBy?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  invoice?: ZeroInvoiceRow | null;
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? null;
}

function toLegacyDate(value: number | string | null | undefined) {
  if (typeof value === "number")
    return new Date(value).toISOString().split("T")[0];
  if (!value) return null;
  return String(value).split("T")[0];
}

function toLegacyInvoice(row: ZeroInvoiceRow): Invoice {
  return {
    id: row.id,
    project_id: row.projectID ?? null,
    source_quote_version_id: row.sourceQuoteVersionID ?? null,
    invoice_number: row.invoiceNumber ?? 0,
    title: row.title,
    language: row.language ?? null,
    currency: row.currency,
    status: row.status,
    invoice_date: toLegacyDate(row.invoiceDate),
    due_date: toLegacyDate(row.dueDate),
    payment_term_days: row.paymentTermDays,
    project_discount_percent: row.projectDiscountPercent,
    vat_rate: row.vatRate,
    deposit_percent: row.depositPercent ?? null,
    qr_reference: row.qrReference ?? null,
    include_qr_bill: row.includeQRBill,
    pdf_url: row.pdfURL ?? null,
    notes: row.notes ?? null,
    total_net: row.totalNet,
    total_gross: row.totalGross,
    created_by: row.createdBy ?? null,
    created_at: toLegacyTimestamp(row.createdAt) ?? "",
    updated_at: toLegacyTimestamp(row.updatedAt) ?? "",
    deleted_at: toLegacyTimestamp(row.deletedAt),
    customer_id: row.customerID ?? null,
    contact_id: row.contactID ?? null,
    customer: row.customer
      ? {
          name: row.customer.name,
          invoice_address: row.customer.invoiceAddress ?? null,
          number: row.customer.number ?? null,
        }
      : null,
    contact: row.contact
      ? {
          first_name: row.contact.firstName,
          last_name: row.contact.lastName,
        }
      : null,
    project: row.project
      ? {
          id: row.project.id,
          name: row.project.name,
          number: row.project.number ?? null,
          language: row.project.language ?? null,
          default_vat_rate: row.project.defaultVatRate ?? null,
          default_discount_percent: row.project.defaultDiscountPercent ?? null,
          event_start_date: toLegacyDate(row.project.eventStartDate),
          event_end_date: toLegacyDate(row.project.eventEndDate),
          planning_start_date: toLegacyDate(row.project.planningStartDate),
          planning_end_date: toLegacyDate(row.project.planningEndDate),
          companies: row.project.companies
            ? {
                name: row.project.companies.name,
                invoice_address: row.project.companies.invoiceAddress ?? null,
                number: row.project.companies.number ?? null,
              }
            : null,
          contact: row.project.contact
            ? {
                first_name: row.project.contact.firstName,
                last_name: row.project.contact.lastName,
              }
            : null,
          account_manager: row.project.account_manager
            ? {
                first_name: row.project.account_manager.firstName,
                last_name: row.project.account_manager.lastName,
              }
            : null,
        }
      : null,
  };
}

function toLegacyInvoiceReminder(row: ZeroInvoiceReminderRow): InvoiceReminder {
  return {
    id: row.id,
    invoice_id: row.invoiceID,
    reminder_number: row.reminderNumber,
    title: row.title,
    reminder_date: toLegacyDate(row.reminderDate) ?? "",
    due_date: toLegacyDate(row.dueDate) ?? "",
    payment_term_days: row.paymentTermDays,
    pdf_url: row.pdfURL ?? null,
    status: row.status,
    total_net: row.totalNet,
    total_gross: row.totalGross,
    created_by: row.createdBy ?? null,
    created_at: toLegacyTimestamp(row.createdAt) ?? "",
    updated_at: toLegacyTimestamp(row.updatedAt) ?? "",
    invoice: row.invoice ? toLegacyInvoice(row.invoice) : undefined,
  };
}

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

function useZeroMutation<TInput, TResult>({
  run,
  success,
  failure,
}: {
  run: (input: TInput) => Promise<TResult>;
  success?: string;
  failure: string;
}) {
  const [isPending, setIsPending] = useState(false);

  const wrappedRun = async (input: TInput) => {
    setIsPending(true);
    try {
      return await run(input);
    } catch (error) {
      throw toFriendlyMutationError(error);
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (input: TInput, options?: MutationOptions<TResult>) => {
      void wrappedRun(input)
        .then((data) => {
          if (success) toast.success(success);
          options?.onSuccess?.(data);
        })
        .catch((error) => toast.error(`${failure}: ${error.message}`));
    },
    mutateAsync: wrappedRun,
    isPending,
  };
}

export function useProjectInvoices(projectId: string | undefined) {
  const [data, details] = useZeroQuery(
    appQueries.invoices.byProject({
      projectID: projectId ?? "00000000-0000-0000-0000-000000000000",
    }),
  );
  const invoices = useMemo(
    () => ((data ?? []) as ZeroInvoiceRow[]).map(toLegacyInvoice),
    [data],
  );

  return {
    data: projectId ? invoices : [],
    isLoading:
      !!projectId && details.type !== "complete" && invoices.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useInvoices() {
  const [data, details] = useZeroQuery(appQueries.invoices.list());
  const invoices = useMemo(
    () => ((data ?? []) as ZeroInvoiceRow[]).map(toLegacyInvoice),
    [data],
  );

  return {
    data: invoices,
    isLoading: details.type !== "complete" && invoices.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useInvoice(invoiceId: string | undefined) {
  const query = invoiceId
    ? appQueries.invoices.byID({ id: invoiceId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!invoiceId });
  const invoice = useMemo(
    () => (invoiceId && data ? toLegacyInvoice(data as ZeroInvoiceRow) : null),
    [data, invoiceId],
  );

  return {
    data: invoice,
    isLoading: !!invoiceId && details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateInvoice() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      projectId?: string | null;
      title: string;
      language?: "de" | "en" | "fr" | null;
      currency?: InvoiceCurrency;
      sourceQuoteVersionId?: string;
      customerId?: string | null;
      contactId?: string | null;
      depositPercent?: number;
      projectDiscountPercent?: number;
      vatRate?: "CH_8_1" | "NONE";
      includeQrBill?: boolean;
    },
    Invoice
  >({
    success: "Invoice created",
    failure: "Failed to create invoice",
    run: async (vars) => {
      const id = generateUUID();
      const now = new Date().toISOString();
      const mutation = zero.mutate(
        appMutators.invoices.create({
          id,
          projectID: vars.projectId ?? null,
          title: vars.title,
          language: vars.language ?? null,
          currency: vars.currency ?? "CHF",
          sourceQuoteVersionID: vars.sourceQuoteVersionId ?? null,
          customerID: vars.customerId ?? null,
          contactID: vars.contactId ?? null,
          depositPercent: vars.depositPercent ?? null,
          projectDiscountPercent: vars.projectDiscountPercent ?? 0,
          vatRate: vars.vatRate ?? "CH_8_1",
          includeQRBill:
            (vars.currency ?? "CHF") === "CHF" && (vars.includeQrBill ?? true),
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Invoice create rejected",
      );
      return toLegacyInvoice({
        id,
        projectID: vars.projectId ?? null,
        sourceQuoteVersionID: vars.sourceQuoteVersionId ?? null,
        customerID: vars.customerId ?? null,
        contactID: vars.contactId ?? null,
        invoiceNumber: 0,
        title: vars.title,
        language: vars.language ?? null,
        currency: vars.currency ?? "CHF",
        status: "draft",
        paymentTermDays: 30,
        projectDiscountPercent: vars.projectDiscountPercent ?? 0,
        vatRate: vars.vatRate ?? "CH_8_1",
        depositPercent: vars.depositPercent ?? null,
        includeQRBill:
          (vars.currency ?? "CHF") === "CHF" && (vars.includeQrBill ?? true),
        totalNet: 0,
        totalGross: 0,
        createdBy: null,
        createdAt: now,
        updatedAt: now,
      });
    },
  });
}

export function useUpdateInvoice() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      id: string;
      updates: Partial<{
        title: string;
        language: "de" | "en" | "fr" | null;
        currency: InvoiceCurrency;
        invoice_date: string | null;
        due_date: string | null;
        payment_term_days: number;
        project_discount_percent: number;
        vat_rate: "CH_8_1" | "NONE";
        deposit_percent: number | null;
        include_qr_bill: boolean;
        pdf_url: string | null;
        notes: string | null;
        total_net: number;
        total_gross: number;
        qr_reference: string | null;
        customer_id: string | null;
        contact_id: string | null;
      }>;
    },
    { id: string }
  >({
    failure: "Failed to update invoice",
    run: async ({ id, updates }) => {
      const mutation = zero.mutate(
        appMutators.invoices.update({
          id,
          title: updates.title,
          language: updates.language,
          currency: updates.currency,
          invoiceDate: updates.invoice_date,
          dueDate: updates.due_date,
          paymentTermDays: updates.payment_term_days,
          projectDiscountPercent: updates.project_discount_percent,
          vatRate: updates.vat_rate,
          depositPercent: updates.deposit_percent,
          includeQRBill: updates.include_qr_bill,
          pdfURL: updates.pdf_url,
          notes: updates.notes,
          totalNet: updates.total_net,
          totalGross: updates.total_gross,
          qrReference: updates.qr_reference,
          customerID: updates.customer_id,
          contactID: updates.contact_id,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Invoice update rejected",
      );
      return { id };
    },
  });
}

export function useDeleteInvoice() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<string, string>({
    success: "Invoice deleted",
    failure: "Failed to delete invoice",
    run: async (id) => {
      const mutation = zero.mutate(appMutators.invoices.delete({ id }));
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Invoice delete rejected",
      );
      return id;
    },
  });
}

export function useTransitionInvoiceStatus() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    { id: string; newStatus: InvoiceStatus },
    { id: string; status: InvoiceStatus }
  >({
    failure: "Failed to update status",
    run: async ({ id, newStatus }) => {
      const mutation = zero.mutate(
        appMutators.invoices.transitionStatus({ id, status: newStatus }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Invoice status update rejected",
      );
      toast.success(`Invoice marked as ${newStatus}`);
      return { id, status: newStatus };
    },
  });
}

export function useInvoiceReminders(invoiceId: string | undefined) {
  const [data, details] = useZeroQuery(
    appQueries.invoiceReminders.byInvoice({
      invoiceID: invoiceId ?? "00000000-0000-0000-0000-000000000000",
    }),
  );
  const reminders = useMemo(
    () =>
      ((data ?? []) as ZeroInvoiceReminderRow[]).map(toLegacyInvoiceReminder),
    [data],
  );

  return {
    data: invoiceId ? reminders : [],
    isLoading:
      !!invoiceId && details.type !== "complete" && reminders.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useInvoiceReminder(reminderId: string | undefined | null) {
  const [data, details] = useZeroQuery(
    appQueries.invoiceReminders.byID({
      id: reminderId ?? "00000000-0000-0000-0000-000000000000",
    }),
    { enabled: !!reminderId },
  );
  const reminder = useMemo(
    () =>
      reminderId && data
        ? toLegacyInvoiceReminder(data as ZeroInvoiceReminderRow)
        : null,
    [data, reminderId],
  );

  return {
    data: reminder,
    isLoading: !!reminderId && details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateInvoiceReminder() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      invoiceId: string;
      title: string;
      reminderDate: string;
      dueDate: string;
      paymentTermDays: number;
      pdfUrl: string | null;
      totalNet: number;
      totalGross: number;
      nextReminderNumber: number;
    },
    InvoiceReminder
  >({
    success: "Payment reminder created",
    failure: "Failed to create payment reminder",
    run: async (vars) => {
      const id = generateUUID();
      const now = new Date().toISOString();
      const mutation = zero.mutate(
        appMutators.invoiceReminders.create({
          id,
          invoiceID: vars.invoiceId,
          title: vars.title,
          reminderDate: vars.reminderDate,
          dueDate: vars.dueDate,
          paymentTermDays: vars.paymentTermDays,
          pdfURL: vars.pdfUrl,
          totalNet: vars.totalNet,
          totalGross: vars.totalGross,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Invoice reminder create rejected",
      );
      return {
        id,
        invoice_id: vars.invoiceId,
        reminder_number: vars.nextReminderNumber,
        title: vars.title,
        reminder_date: vars.reminderDate,
        due_date: vars.dueDate,
        payment_term_days: vars.paymentTermDays,
        pdf_url: vars.pdfUrl,
        status: "sent",
        total_net: vars.totalNet,
        total_gross: vars.totalGross,
        created_by: null,
        created_at: now,
        updated_at: now,
      };
    },
  });
}

export function useDeleteInvoiceReminder() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<string, { id: string }>({
    success: "Payment reminder deleted",
    failure: "Failed to delete payment reminder",
    run: async (id) => {
      const mutation = zero.mutate(appMutators.invoiceReminders.delete({ id }));
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Invoice reminder delete rejected",
      );
      return { id };
    },
  });
}

export function useQuoteInvoiceSummary(
  quoteVersionId: string | undefined | null,
) {
  const [data, details] = useZeroQuery(
    appQueries.invoices.byQuoteVersion({
      quoteVersionID: quoteVersionId ?? "00000000-0000-0000-0000-000000000000",
    }),
  );
  const invoices = ((data ?? []) as ZeroInvoiceRow[]).map((row) => {
    const invoice = toLegacyInvoice(row);
    const vatRate = invoice.vat_rate === "CH_8_1" ? 0.081 : 0;
    return {
      id: invoice.id,
      invoice_number: invoice.invoice_number,
      title: invoice.title,
      currency: invoice.currency,
      status: invoice.status,
      deposit_percent: invoice.deposit_percent,
      total_gross: invoice.total_gross,
      vat_rate: invoice.vat_rate,
      invoiced_net:
        Math.round((invoice.total_gross / (1 + vatRate)) * 100) / 100,
    };
  });

  return {
    data: quoteVersionId ? invoices : [],
    isLoading:
      !!quoteVersionId && details.type !== "complete" && invoices.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}
